import { z } from "zod";
import { rangIdSchema } from "./viewers";

/**
 * Le contrat avec L'Ecuyer, le bot Discord de La Kavalry (ticket 2 du socle, plan
 * `docs/plans/2026-10-31-castellan-viewers.md`, taches 2.2 a 2.5). Il parle a Castellan par `/api/ecuyer/*`, avec
 * le jeton `ECUYER_TOKEN`. Tout est en POST : la couche du Lab compte chaque GET comme une visite, et L'Ecuyer
 * relit l'etat chaque minute.
 *
 * Aucun identifiant n'est un nombre : un identifiant Discord ou un `bigint` de la base voyage en texte.
 */

/** Un identifiant Discord (un « snowflake ») : 17 a 20 chiffres. */
export const discordIdSchema = z.string().regex(/^\d{17,20}$/);

/** Les motifs de gain que L'Ecuyer sait envoyer (le bareme est le sien). `import` n'a qu'une voie : l'import. */
export const MOTIFS_DISCORD = [
  "jour_actif",
  "vote",
  "quete",
  "idee",
  "bon_plan",
  "enigme",
] as const;

/** Un gain vaut de 1 a 50 points : au-dela, c'est une erreur de L'Ecuyer (l'enigme, le plus gros gain, vaut 15). */
export const POINTS_GAIN_MAX = 50;
export const GAINS_PAR_LOT_MAX = 500;
export const MEMBRES_IMPORT_MAX = 5_000;
export const MONTEES_PAR_PAGE = 100;

export const gainsRequeteSchema = z.object({
  gains: z
    .array(
      z.object({
        /** Stable cote L'Ecuyer : jamais deux fois le meme gain. */
        id: z.string().regex(/^[A-Za-z0-9:._-]{1,128}$/),
        discordId: discordIdSchema,
        pseudo: z.string().min(1).max(64),
        motif: z.enum(MOTIFS_DISCORD),
        points: z.number().int().min(1).max(POINTS_GAIN_MAX),
        survenuLe: z.iso.datetime({ offset: true }),
      }),
    )
    .min(1)
    .max(GAINS_PAR_LOT_MAX),
});
export type GainsRequete = z.infer<typeof gainsRequeteSchema>;

export const gainsReponseSchema = z.object({
  acceptes: z.number().int(),
  doublons: z.number().int(),
  refuses: z.array(z.object({ id: z.string(), raison: z.enum(["opposition"]) })),
});
export type GainsReponse = z.infer<typeof gainsReponseSchema>;

/** L'etat vivant du registre `live` de L'Ecuyer, envoye une fois a la bascule. */
export const importKavalrySchema = z.object({
  version: z.literal(1),
  sauvegardeLe: z.iso.datetime({ offset: true }),
  membres: z
    .array(
      z.object({
        discordId: discordIdSchema,
        pseudo: z.string().min(1).max(64),
        points: z.number().int().min(0).max(100_000),
        compteurs: z.object({
          quetes: z.number().int().min(0).max(1_000_000),
          votes: z.number().int().min(0).max(1_000_000),
          idees: z.number().int().min(0).max(1_000_000),
          jours: z.number().int().min(0).max(1_000_000),
        }),
      }),
    )
    .max(MEMBRES_IMPORT_MAX),
});
export type ImportKavalry = z.infer<typeof importKavalrySchema>;

export const importReponseSchema = z.object({
  importes: z.number().int(),
  dejaImportes: z.number().int(),
  /** Une opposition, ou 0 point. */
  ignores: z.number().int(),
  montees: z.number().int(),
});
export type ImportReponse = z.infer<typeof importReponseSchema>;

/** `POST /api/ecuyer/renommee/etat` : tout l'etat que L'Ecuyer affiche, lu chaque minute au plus. Corps `{}`. */
export const etatRequeteSchema = z.object({}).strict();

export const renommeeEcuyerSchema = z.object({
  rangs: z.array(
    z.object({ id: rangIdSchema, nom: z.string(), seuil: z.number().int(), couleur: z.string() }),
  ),
  membres: z.array(
    z.object({
      discordId: z.string(),
      /** Twitch et Discord additionnes pour un profil lie. */
      renommee: z.number().int(),
      /** Le rang atteint, jamais en dessous. */
      rang: rangIdSchema,
      /** Parmi les profils qui ont une identite Discord ; `null` a 0 point. Les ex aequo ont la meme place. */
      position: z.number().int().nullable(),
      twitchLie: z.boolean(),
      compteurs: z.object({
        quetes: z.number().int(),
        votes: z.number().int(),
        idees: z.number().int(),
        jours: z.number().int(),
        streams: z.number().int(),
        subs: z.number().int(),
      }),
    }),
  ),
  lu_le: z.string(),
});
export type RenommeeEcuyer = z.infer<typeof renommeeEcuyerSchema>;

/** `POST /api/ecuyer/montees` : les montees des profils qui ont une identite Discord, dans l'ordre. */
export const monteesRequeteSchema = z.object({
  /** Le dernier `id` deja annonce ; absent, depuis le debut. */
  apres: z
    .string()
    .regex(/^\d{1,18}$/)
    .optional(),
});
export type MonteesRequete = z.infer<typeof monteesRequeteSchema>;

export const monteesEcuyerSchema = z.object({
  montees: z.array(
    z.object({
      id: z.string(),
      discordId: z.string(),
      rangAvant: rangIdSchema,
      rangApres: rangIdSchema,
      survenueLe: z.string(),
    }),
  ),
});
export type MonteesEcuyer = z.infer<typeof monteesEcuyerSchema>;

/** `POST /api/viewers/opposition-discord` (la regie) : efface un membre Discord a sa demande. */
export const oppositionDiscordRequeteSchema = z.object({ discordId: discordIdSchema });
export type OppositionDiscordRequete = z.infer<typeof oppositionDiscordRequeteSchema>;
export const oppositionDiscordReponseSchema = z.object({ issue: z.enum(["efface", "inscrit"]) });
export type OppositionDiscordReponse = z.infer<typeof oppositionDiscordReponseSchema>;

/**
 * `POST /api/viewers/opposition-discord/lever` (la regie) : le retour d'un membre Discord, comme
 * `POST /api/twitch/opposition/lever` pour Twitch. Le corps est l'identifiant Discord (17 a 20 chiffres : une opposition ne
 * garde que lui, jamais un pseudo). Les resultats sont ceux de la levee Twitch, sous le nom `issue` des routes Discord, et la
 * reponse ne dit jamais qui :
 * - `levee` : le membre avait une opposition, elle est retiree ; un gain de L'Ecuyer pour lui est de nouveau accepte, et il
 *   obtient de nouveau un code de liaison. Ce qui a ete efface ne revient pas ;
 * - `absente` : il n'avait pas d'opposition (ou elle est deja levee) : rien n'a change.
 */
export const oppositionDiscordLeveeRequeteSchema = z.object({ discordId: discordIdSchema });
export type OppositionDiscordLeveeRequete = z.infer<typeof oppositionDiscordLeveeRequeteSchema>;
export const OPPOSITION_DISCORD_LEVEE_ISSUES = ["levee", "absente"] as const;
export type OppositionDiscordLeveeIssue = (typeof OPPOSITION_DISCORD_LEVEE_ISSUES)[number];
export const oppositionDiscordLeveeReponseSchema = z.object({
  issue: z.enum(OPPOSITION_DISCORD_LEVEE_ISSUES),
});
export type OppositionDiscordLeveeReponse = z.infer<typeof oppositionDiscordLeveeReponseSchema>;

/**
 * `POST /api/ecuyer/liaisons` : L'Ecuyer demande un code de liaison pour un membre (sa commande `/lier`) et le montre au
 * membre en prive. Le membre le tape dans le chat Twitch (`!lier K7P2`). 4 caracteres sans ambigus, valable 10 minutes,
 * a usage unique, un seul code vivant par membre. 409 `opposition` si le membre s'est oppose.
 */
export const liaisonDemandeSchema = z.object({
  discordId: discordIdSchema,
  pseudo: z.string().min(1).max(64),
});
export type LiaisonDemande = z.infer<typeof liaisonDemandeSchema>;

export const liaisonReponseSchema = z.object({
  code: z.string().regex(/^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}$/),
  expireLe: z.iso.datetime({ offset: true }),
});
export type LiaisonReponse = z.infer<typeof liaisonReponseSchema>;

/** `POST /api/ecuyer/liaisons/delier` : L'Ecuyer defait la liaison d'un membre (sa commande `/delier`). */
export const delierRequeteSchema = z.object({ discordId: discordIdSchema });
export type DelierRequete = z.infer<typeof delierRequeteSchema>;
export const delierReponseSchema = z.object({ issue: z.enum(["delie", "pas_relie"]) });
export type DelierReponse = z.infer<typeof delierReponseSchema>;

/** `POST /api/viewers/{id}/delier` (la regie) : defait la liaison d'un viewer depuis sa fiche. */
export const delierViewerReponseSchema = z.object({ issue: z.enum(["delie", "pas_relie"]) });
export type DelierViewerReponse = z.infer<typeof delierViewerReponseSchema>;
